import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { RiskBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const emptyPC = {
  child_name: "", child_age: 10, child_identifier: "",
  parent_name: "", parent_email: "", parent_phone: "", relationship: "parent",
  verification_method: "aadhaar_ekyc", verification_evidence_url: "",
  purposes: [], consent_text: "I consent to the processing of my child's personal data as described.",
  activity_id: "", template_id: "", expires_in_days: 365,
};

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    implemented: { bg: "var(--success-soft)", color: "var(--success)" },
    partial: { bg: "var(--warn-soft)", color: "var(--warn)" },
    not_started: { bg: "var(--danger-soft)", color: "var(--danger)" },
    not_applicable: { bg: "var(--bg-2)", color: "var(--muted)" },
    active: { bg: "var(--success-soft)", color: "var(--success)" },
    withdrawn: { bg: "var(--danger-soft)", color: "var(--danger)" },
    expired: { bg: "var(--bg-2)", color: "var(--muted)" },
  };
  const t = map[status] || map.not_applicable;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

export default function ChildrenData() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin", "dpo"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [pconsents, setPconsents] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pcModal, setPcModal] = useState(false);
  const [pcForm, setPcForm] = useState(emptyPC);

  const load = async () => {
    setLoading(true);
    try {
      const [d, pc, m] = await Promise.all([
        api.get("/children/dashboard"),
        api.get("/children/parental-consents"),
        api.get("/children/age-verification-methods"),
      ]);
      setDash(d.data);
      setPconsents(pc.data.items || []);
      setMethods(m.data.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const savePC = async () => {
    try {
      await api.post("/children/parental-consent", {
        ...pcForm,
        purposes: (typeof pcForm.purposes === "string" ? pcForm.purposes.split(",").map(s => s.trim()).filter(Boolean) : pcForm.purposes),
      });
      toast.success("Parental consent recorded");
      setPcModal(false);
      setPcForm(emptyPC);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const withdrawPC = async (pc) => {
    const reason = window.prompt("Reason (optional):", "");
    try {
      await api.post(`/children/parental-consent/${pc.id}/withdraw`, { reason });
      toast.success("Withdrawn"); await load();
    } catch { toast.error("Failed"); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/children/compliance-report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "children_data_compliance.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Download failed"); }
  };

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Children's Data Compliance"
        kicker="DPDP Sec. 9 Special Obligations"
        description="Verifiable parental consent, age verification and compliance checklist for processing children's data."
        testId="children-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="children-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            <Btn variant="secondary" onClick={downloadReport} data-testid="children-download-pdf">
              <Icons.Download className="w-3.5 h-3.5" /> Download report
            </Btn>
            {isAdmin && tab === "parental-consents" && (
              <Btn onClick={() => { setPcForm(emptyPC); setPcModal(true); }} data-testid="pc-new-btn">
                <Icons.Plus className="w-3.5 h-3.5" /> Record parental consent
              </Btn>
            )}
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "parental-consents", label: "Parental consents", icon: "ShieldCheck" },
          { key: "verification", label: "Age verification", icon: "BadgeCheck" },
          { key: "checklist", label: "Sec. 9 Checklist", icon: "ListChecks" },
        ].map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`children-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Sec. 9 Compliance score" value={`${dash.score}%`} tone={dash.score >= 80 ? "success" : dash.score >= 50 ? "warn" : "danger"} testId="stat-score" />
            <StatCard label="Child data assets" value={dash.total_child_assets} testId="stat-assets" />
            <StatCard label="Child activities" value={dash.total_child_activities} testId="stat-activities" hint={`${dash.activities_with_dpia} have DPIA`} />
            <StatCard label="Parental consents" value={dash.total_parental_consents} tone="success" testId="stat-pconsents" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Data assets containing children&apos;s data</div>
              {(dash.assets || []).length === 0 ? (
                <div className="text-[12px] muted">No child-data assets flagged.</div>
              ) : (
                <div className="space-y-2" data-testid="child-assets-list">
                  {dash.assets.map(a => (
                    <div key={a.id} className="p-3 rounded-md flex items-start justify-between" style={{ border: "1px solid var(--border)" }}>
                      <div>
                        <div className="text-[13px] font-medium ink">{a.name}</div>
                        <div className="text-[10.5px] muted">{a.asset_type} · {a.hosting_location || "—"}</div>
                      </div>
                      <RiskBadge level={a.risk_rating} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Verification methods usage</div>
              {Object.keys(dash.by_verification_method || {}).length === 0 ? (
                <div className="text-[12px] muted">No parental consents recorded yet.</div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(dash.by_verification_method).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <div className="flex-1 text-[12px] ink">{k.replace(/_/g, " ")}</div>
                      <div className="text-[12px] muted">{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "parental-consents" && (
        pconsents.length === 0 ? (
          <EmptyState title="No parental consents yet"
            hint="Record verifiable parental consent for any child under 18 whose data you process."
            action={isAdmin && <Btn onClick={() => setPcModal(true)}><Icons.Plus className="w-4 h-4" /> Record consent</Btn>} />
        ) : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["Child", "Age", "Parent", "Verification", "Status", "Granted", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="pconsents-tbody">
                {pconsents.map(pc => (
                  <tr key={pc.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td className="px-4 py-2 ink text-[12px]">
                      <div>{pc.child_name}</div>
                      <div className="text-[10.5px] muted">{pc.child_identifier}</div>
                    </td>
                    <td className="px-4 py-2 ink text-[12px]">{pc.child_age}</td>
                    <td className="px-4 py-2 ink text-[12px]">
                      <div>{pc.parent_name}</div>
                      <div className="text-[10.5px] muted">{pc.parent_email}</div>
                    </td>
                    <td className="px-4 py-2 text-[11px]">
                      <div className="flex items-center gap-1">
                        {pc.verification_verified && <Icons.CheckCircle className="w-3 h-3" style={{ color: "var(--success)" }} />}
                        {pc.verification_method?.replace(/_/g, " ")}
                      </div>
                    </td>
                    <td className="px-4 py-2"><StatusPill status={pc.status} /></td>
                    <td className="px-4 py-2 muted text-[11px]">{pc.granted_at?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right">
                      {isAdmin && pc.status === "active" && (
                        <button onClick={() => withdrawPC(pc)} className="text-[11px] font-medium hover:underline"
                          style={{ color: "var(--danger)" }} data-testid={`pc-withdraw-${pc.id}`}>
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "verification" && (
        <div className="grid gap-3" data-testid="methods-list">
          {methods.map(m => (
            <div key={m.id} className="surface rounded-xl p-4 flex items-center gap-4" style={{ border: "1px solid var(--border)" }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                <Icons.BadgeCheck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold ink text-[13px]">{m.method.replace(/_/g, " ")}</div>
                  {m.evidence_required && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>Evidence required</span>}
                  {!m.active && <StatusPill status="not_applicable" />}
                </div>
                <div className="text-[11.5px] muted mt-1">{m.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "checklist" && dash && (
        <div className="grid gap-3" data-testid="checklist-list">
          {dash.checklist.map((c, i) => (
            <div key={i} className="surface rounded-xl p-4 flex items-start gap-3" style={{ border: "1px solid var(--border)" }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                style={{
                  background: c.status === "implemented" ? "var(--success-soft)" : c.status === "partial" ? "var(--warn-soft)" : "var(--danger-soft)",
                  color: c.status === "implemented" ? "var(--success)" : c.status === "partial" ? "var(--warn)" : "var(--danger)",
                }}>
                {c.status === "implemented" ? <Icons.Check className="w-4 h-4" /> : c.status === "partial" ? <Icons.AlertTriangle className="w-4 h-4" /> : <Icons.X className="w-4 h-4" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold ink text-[13px]">{c.title}</div>
                  <span className="text-[10px] mono px-1.5 py-0.5 rounded" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>{c.dpdp}</span>
                </div>
                <div className="mt-1"><StatusPill status={c.status} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Record parental consent */}
      <Dialog open={pcModal} onOpenChange={setPcModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Record verifiable parental consent</DialogTitle>
            <DialogDescription>DPDP Sec. 9 requires verifiable parental / guardian consent for processing children&apos;s data.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto">
            <Field label="Child name" required><TextInput data-testid="pc-child-name" value={pcForm.child_name} onChange={(e) => setPcForm({ ...pcForm, child_name: e.target.value })} /></Field>
            <Field label="Child age" required><TextInput type="number" min={0} max={17} data-testid="pc-child-age" value={pcForm.child_age} onChange={(e) => setPcForm({ ...pcForm, child_age: Number(e.target.value) })} /></Field>
            <Field label="Child identifier (student ID / phone)" className="col-span-2"><TextInput data-testid="pc-child-id" value={pcForm.child_identifier} onChange={(e) => setPcForm({ ...pcForm, child_identifier: e.target.value })} /></Field>
            <Field label="Parent / Guardian name" required><TextInput data-testid="pc-parent-name" value={pcForm.parent_name} onChange={(e) => setPcForm({ ...pcForm, parent_name: e.target.value })} /></Field>
            <Field label="Relationship"><SelectInput data-testid="pc-relationship" value={pcForm.relationship} onChange={(e) => setPcForm({ ...pcForm, relationship: e.target.value })}>
              <option value="parent">Parent</option><option value="legal_guardian">Legal guardian</option>
            </SelectInput></Field>
            <Field label="Parent email" required><TextInput type="email" data-testid="pc-parent-email" value={pcForm.parent_email} onChange={(e) => setPcForm({ ...pcForm, parent_email: e.target.value })} /></Field>
            <Field label="Parent phone"><TextInput data-testid="pc-parent-phone" value={pcForm.parent_phone} onChange={(e) => setPcForm({ ...pcForm, parent_phone: e.target.value })} /></Field>
            <Field label="Verification method" required className="col-span-2">
              <SelectInput data-testid="pc-method" value={pcForm.verification_method} onChange={(e) => setPcForm({ ...pcForm, verification_method: e.target.value })}>
                {methods.map(m => <option key={m.method} value={m.method}>{m.method.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Verification evidence URL" className="col-span-2"><TextInput data-testid="pc-evidence" value={pcForm.verification_evidence_url} onChange={(e) => setPcForm({ ...pcForm, verification_evidence_url: e.target.value })} placeholder="e.g. /uploads/parent-id-scan.pdf" /></Field>
            <Field label="Purposes (comma-sep)" className="col-span-2"><TextInput data-testid="pc-purposes" value={Array.isArray(pcForm.purposes) ? pcForm.purposes.join(", ") : pcForm.purposes}
              onChange={(e) => setPcForm({ ...pcForm, purposes: e.target.value })} /></Field>
            <Field label="Consent text" className="col-span-2"><TextArea data-testid="pc-text" value={pcForm.consent_text} onChange={(e) => setPcForm({ ...pcForm, consent_text: e.target.value })} /></Field>
            <Field label="Expires in (days)"><TextInput type="number" data-testid="pc-expires" value={pcForm.expires_in_days || ""} onChange={(e) => setPcForm({ ...pcForm, expires_in_days: Number(e.target.value) || null })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setPcModal(false)} data-testid="pc-cancel">Cancel</Btn>
            <Btn onClick={savePC} disabled={!pcForm.child_name || !pcForm.parent_name || !pcForm.parent_email} data-testid="pc-save">Record consent</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
